import type { Editor } from '@tiptap/react';

// ── Option type ──────────────────────────────────────────────────────────────

export interface SlashCommand {
  label: string;
  icon: string;
  category: string;
  description: string;
  run: (editor: Editor) => void;
}

// ── Commands — mirrors legacy trigger-plugin category order ──────────────────

export const SLASH_COMMANDS: SlashCommand[] = [
  // Turn into
  { label: 'Heading 1',    icon: 'format_h1',        category: 'Turn into', description: 'Large section heading',     run: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { label: 'Heading 2',    icon: 'format_h2',        category: 'Turn into', description: 'Medium section heading',    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { label: 'Heading 3',    icon: 'format_h3',        category: 'Turn into', description: 'Small section heading',     run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { label: 'Paragraph',    icon: 'format_paragraph', category: 'Turn into', description: 'Plain text block',          run: (e) => e.chain().focus().setParagraph().run() },
  { label: 'Quote',        icon: 'format_quote',     category: 'Turn into', description: 'Highlighted blockquote',    run: (e) => e.chain().focus().toggleBlockquote().run() },
  { label: 'Code block',   icon: 'data_object',      category: 'Turn into', description: 'Monospaced code block',     run: (e) => e.chain().focus().toggleCodeBlock().run() },

  // Lists
  { label: 'Bulleted list', icon: 'format_list_bulleted', category: 'Lists', description: 'Unordered bullet list',  run: (e) => e.chain().focus().toggleBulletList().run() },
  { label: 'Numbered list', icon: 'format_list_numbered', category: 'Lists', description: 'Ordered numbered list',  run: (e) => e.chain().focus().toggleOrderedList().run() },
  { label: 'Checklist',     icon: 'checklist',             category: 'Lists', description: 'Checkable to-do list',  run: (e) => e.chain().focus().toggleTaskList().run() },

  // Format
  { label: 'Bold',          icon: 'format_bold',       category: 'Format', description: 'Make text bold',            run: (e) => e.chain().focus().toggleBold().run() },
  { label: 'Italic',        icon: 'format_italic',     category: 'Format', description: 'Make text italic',          run: (e) => e.chain().focus().toggleItalic().run() },
  { label: 'Underline',     icon: 'format_underlined', category: 'Format', description: 'Underline text',            run: (e) => e.chain().focus().toggleUnderline().run() },
  { label: 'Strikethrough', icon: 'strikethrough_s',   category: 'Format', description: 'Strike through text',       run: (e) => e.chain().focus().toggleStrike().run() },
  { label: 'Inline code',   icon: 'code',              category: 'Format', description: 'Monospaced inline code',    run: (e) => e.chain().focus().toggleCode().run() },
  { label: 'Highlight',     icon: 'ink_highlighter',   category: 'Format', description: 'Highlight text in yellow',  run: (e) => e.chain().focus().toggleHighlight().run() },
  { label: 'Subscript',     icon: 'subscript',         category: 'Format', description: 'Subscript text (H₂O)',      run: (e) => e.chain().focus().toggleSubscript().run() },
  { label: 'Superscript',   icon: 'superscript',       category: 'Format', description: 'Superscript text (x²)',     run: (e) => e.chain().focus().toggleSuperscript().run() },

  // Align
  { label: 'Align left',   icon: 'format_align_left',    category: 'Align', description: 'Align block to the left',  run: (e) => e.chain().focus().setTextAlign('left').run() },
  { label: 'Align center', icon: 'format_align_center',  category: 'Align', description: 'Center the block',         run: (e) => e.chain().focus().setTextAlign('center').run() },
  { label: 'Align right',  icon: 'format_align_right',   category: 'Align', description: 'Align block to the right', run: (e) => e.chain().focus().setTextAlign('right').run() },
  { label: 'Justify',      icon: 'format_align_justify', category: 'Align', description: 'Justify block text',       run: (e) => e.chain().focus().setTextAlign('justify').run() },

  // Insert
  { label: 'Table',        icon: 'table',           category: 'Insert', description: 'Insert a data table',          run: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { label: 'Image',        icon: 'image',           category: 'Insert', description: 'Upload an image from disk',    run: () => window.dispatchEvent(new Event('nula-show-image-picker')) },
  // setYoutubeVideo is from @tiptap/extension-youtube — cast (pnpm augmentation gap)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { label: 'YouTube',      icon: 'smart_display',   category: 'Insert', description: 'Embed a YouTube video by URL', run: (e) => { const url = window.prompt('YouTube URL'); if (url) (e.chain().focus() as any).setYoutubeVideo({ src: url }).run(); } },
  { label: 'Divider',      icon: 'horizontal_rule', category: 'Insert', description: 'Horizontal divider line',      run: (e) => e.chain().focus().setHorizontalRule().run() },
  { label: 'Canvas',       icon: 'gesture',         category: 'Insert', description: 'Embed a new or existing canvas', run: () => window.dispatchEvent(new Event('nula-show-canvas-picker')) },
  // insertCollapsible is typed on the CollapsibleExtension — cast to avoid pnpm workspace augmentation gaps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { label: 'Collapsible',  icon: 'expand_more',     category: 'Insert', description: 'Collapsible toggle section',   run: (e) => (e.chain().focus() as any).insertCollapsible().run() },

  // Callout
  { label: 'Info callout',    icon: 'info',         category: 'Callout', description: 'Blue informational callout block', run: (e) => e.chain().focus().insertCallout('info').run() },
  { label: 'Warning callout', icon: 'warning',      category: 'Callout', description: 'Yellow warning callout block',     run: (e) => e.chain().focus().insertCallout('warning').run() },
  { label: 'Success callout', icon: 'check_circle', category: 'Callout', description: 'Green success callout block',      run: (e) => e.chain().focus().insertCallout('success').run() },
  { label: 'Danger callout',  icon: 'error',        category: 'Callout', description: 'Red danger callout block',         run: (e) => e.chain().focus().insertCallout('danger').run() },

  // Annotate
  { label: 'Link', icon: 'link', category: 'Annotate', description: 'Add a hyperlink to selection', run: () => window.dispatchEvent(new Event('nula-editor-open-link')) },
];

// ── Default labels (no-query / browse view) ──────────────────────────────────
const DEFAULT_LABELS = [
  'Heading 1',
  'Heading 2',
  'Heading 3',
  'Bulleted list',
  'Numbered list',
  'Checklist',
  'Quote',
  'Code block',
  'Table',
  'Divider',
  'Info callout',
  'Warning callout',
  'Success callout',
  'Danger callout',
];

// ── Fuzzy scoring ─────────────────────────────────────────────────────────────
function scoreCommand(cmd: SlashCommand, query: string): number {
  const q = query.toLowerCase();
  const label = cmd.label.toLowerCase();
  const category = cmd.category.toLowerCase();

  if (label === q) return 100;
  if (label.startsWith(q)) return 90;
  if (label.split(/[\s:]+/).some((w) => w.startsWith(q))) return 80;
  if (label.includes(q)) return 70;
  if (category.startsWith(q)) return 60;
  if (category.includes(q)) return 50;
  if (cmd.description.toLowerCase().includes(q)) return 40;

  let li = 0;
  for (let qi = 0; qi < q.length; qi++) {
    li = label.indexOf(q[qi], li);
    if (li === -1) return 0;
    li++;
  }
  return 20;
}

export function filterCommands(query: string): SlashCommand[] {
  if (!query) {
    return SLASH_COMMANDS.filter((c) => DEFAULT_LABELS.includes(c.label));
  }
  return SLASH_COMMANDS
    .map((c) => ({ c, score: scoreCommand(c, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ c }) => c);
}
